import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Table, TreeSelect, Select, Input, Checkbox, Button, Space, Typography, message } from 'antd';
import { CloseOutlined, CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { buildAppTreeSelectData } from '../mockData';
import { flattenCategoryOptions } from './categoryTreeUtils';
import { DomesticPrice, OverseasPrice, CostPrice } from '../../components/PackagePriceCells';

const { Text } = Typography;

const DEFAULT_SETTING = { upApps: [], domestic: false, overseas: false, categoryId: undefined };
const getDirectionText = (d) => (d === 1 ? '设备' : '用户');
const requiredMark = (label) => <span>{label} <span style={{ color: '#ff4d4f' }}>*</span></span>;

/**
 * 自定义售卖 - 选择套餐弹窗
 * 勾选本企业套餐后，可逐个设置：上架 App（复选树，必选）/ 售卖区域（国内、海外至少选一）/ 归属分类
 * 已添加的套餐不再展示
 */
const AddSalePackageDialog = ({ open, sourcePackages = [], categories = [], existingIds = [], onCancel, onConfirm }) => {
    const [selectedIds, setSelectedIds] = useState([]);
    const [settings, setSettings] = useState({});
    const [nameKeyword, setNameKeyword] = useState('');
    const [idKeyword, setIdKeyword] = useState('');

    const appTreeData = useMemo(() => buildAppTreeSelectData(), []);
    const categoryOptions = useMemo(() => flattenCategoryOptions(categories), [categories]);

    useEffect(() => {
        if (open) {
            setSelectedIds([]);
            setSettings({});
            setNameKeyword('');
            setIdKeyword('');
        }
    }, [open]);

    const getSetting = (id) => ({ ...DEFAULT_SETTING, ...(settings[id] || {}) });
    const updateSetting = (id, patch) => setSettings((prev) => ({ ...prev, [id]: { ...getSetting(id), ...patch } }));

    const filtered = sourcePackages.filter((p) => {
        if (existingIds.includes(p.packageId)) return false;
        if (nameKeyword.trim() && !p.name.toLowerCase().includes(nameKeyword.trim().toLowerCase())) return false;
        if (idKeyword.trim() && !p.packageId.toLowerCase().includes(idKeyword.trim().toLowerCase())) return false;
        return true;
    });

    const handleConfirm = () => {
        const invalid = selectedIds.some((pid) => {
            const setting = getSetting(pid);
            return !setting.upApps.length || !(setting.domestic || setting.overseas);
        });
        if (invalid) {
            message.error('请为已勾选的套餐设置「上架 App」，并至少选择一个售卖区域');
            return;
        }
        const rows = selectedIds.map((pid) => ({ packageId: pid, ...getSetting(pid) }));
        onConfirm(rows);
        setSelectedIds([]);
        setSettings({});
    };

    const columns = [
        {
            title: '套餐信息', key: 'package', width: 200,
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{r.name}</div>
                    <span style={{ fontSize: 12, color: '#bfbfbf', fontFamily: 'monospace' }}>{r.packageId}</span>
                </div>
            ),
        },
        { title: '应用方向', key: 'direction', width: 90, align: 'center', render: (_, r) => getDirectionText(r.applicationDirection) },
        {
            title: requiredMark('上架 App'), key: 'upApps', width: 240,
            render: (_, r) => (
                <TreeSelect
                    treeData={appTreeData}
                    size="small"
                    treeCheckable
                    treeDefaultExpandAll
                    showSearch
                    treeNodeFilterProp="title"
                    showCheckedStrategy={TreeSelect.SHOW_CHILD}
                    placeholder="选择上架 App"
                    style={{ width: '100%' }}
                    maxTagCount="responsive"
                    value={getSetting(r.packageId).upApps}
                    onChange={(v) => updateSetting(r.packageId, { upApps: v })}
                />
            ),
        },
        {
            title: requiredMark('售卖区域'), key: 'sellRegion', width: 170,
            render: (_, r) => {
                const setting = getSetting(r.packageId);
                return (
                    <Space size={12}>
                        <Checkbox checked={setting.domestic} onChange={(e) => updateSetting(r.packageId, { domestic: e.target.checked })}>国内</Checkbox>
                        <Checkbox checked={setting.overseas} onChange={(e) => updateSetting(r.packageId, { overseas: e.target.checked })}>海外</Checkbox>
                    </Space>
                );
            },
        },
        {
            title: '归属分类', key: 'category', width: 180,
            render: (_, r) => (
                <Select
                    size="small" allowClear placeholder="未分类" style={{ width: '100%' }}
                    value={getSetting(r.packageId).categoryId}
                    onChange={(v) => updateSetting(r.packageId, { categoryId: v })}
                    options={categoryOptions}
                />
            ),
        },
        { title: '国内售价', key: 'cny', width: 110, render: (_, r) => <DomesticPrice pkg={r} /> },
        { title: '海外售价', key: 'usd', width: 170, render: (_, r) => <OverseasPrice pkg={r} /> },
        { title: '套餐底价', key: 'cost', width: 110, render: (_, r) => <CostPrice pkg={r} /> },
    ];

    return (
        <Modal
            title="选择套餐"
            open={open}
            onCancel={onCancel}
            width={1120}
            maskClosable={false}
            destroyOnClose
            footer={(
                <Space>
                    <Button icon={<CloseOutlined />} onClick={onCancel}>取消</Button>
                    <Button type="primary" icon={<CheckOutlined />} disabled={!selectedIds.length} onClick={handleConfirm}>
                        确定添加（{selectedIds.length}）
                    </Button>
                </Space>
            )}
        >
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                <Input
                    placeholder="套餐名称" allowClear prefix={<SearchOutlined />} style={{ width: 200 }}
                    value={nameKeyword} onChange={(e) => setNameKeyword(e.target.value)}
                />
                <Input
                    placeholder="套餐ID" allowClear prefix={<SearchOutlined />} style={{ width: 200 }}
                    value={idKeyword} onChange={(e) => setIdKeyword(e.target.value)}
                />
            </div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
                勾选套餐后，设置「上架 App」与「售卖区域」（国内、海外至少选一），并可指定归属分类。
            </Text>
            <Table
                rowKey="packageId"
                size="small"
                dataSource={filtered}
                columns={columns}
                bordered={false}
                pagination={false}
                scroll={{ x: 1280, y: 420 }}
                rowSelection={{ selectedRowKeys: selectedIds, onChange: setSelectedIds }}
                locale={{ emptyText: <Text type="secondary">暂无可选套餐</Text> }}
            />
        </Modal>
    );
};

export default AddSalePackageDialog;
